import React from "react";
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import { firebaseEnv } from "../firebase";

type MatrixRow = {
  path: string;
  role: string;
  read: string;
  list: string;
  create: string;
  update: string;
  delete: string;
  notes?: string;
};

type MatrixGroup = {
  title: string;
  rows: MatrixRow[];
};

const MATRIX: MatrixGroup[] = [
  {
    title: "Users",
    rows: [
      {
        path: "/users/{userId}",
        role: "authenticated",
        read: "yes",
        list: "yes",
        create: "self only",
        update: "self only",
        delete: "no",
        notes: "All signed-in users can read profile docs.",
      },
    ],
  },
  {
    title: "Surveys",
    rows: [
      {
        path: "/surveys/{surveyId}",
        role: "owner",
        read: "yes",
        list: "yes (auth)",
        create: "self owner only",
        update: "owner unchanged",
        delete: "yes",
        notes: "Owner can update only if effective owner identity remains the same.",
      },
      {
        path: "/surveys/{surveyId}",
        role: "editor",
        read: "yes",
        list: "yes (auth)",
        create: "no",
        update: "content only",
        delete: "no",
        notes: "Cannot change userId/ownerId/roles/memberIds. Effective owner must remain unchanged.",
      },
      {
        path: "/surveys/{surveyId}",
        role: "viewer",
        read: "yes",
        list: "yes (auth)",
        create: "no",
        update: "no",
        delete: "no",
        notes: "Read allowed via roles or memberIds access.",
      },
      {
        path: "/surveys/{surveyId}",
        role: "admin",
        read: "yes",
        list: "yes (auth)",
        create: "self owner only",
        update: "yes",
        delete: "yes",
        notes: "Admins can transfer ownership via update.",
      },
      {
        path: "/surveys/{surveyId}",
        role: "public/anonymous",
        read: "published surveys only",
        list: "no",
        create: "no",
        update: "no",
        delete: "no",
        notes: "Allows document get only when published; list remains auth-only.",
      },
      {
        path: "/surveys/{surveyId}/responses/{responseId}",
        role: "owner/editor/admin",
        read: "yes",
        list: "yes",
        create: "yes",
        update: "yes",
        delete: "no",
      },
      {
        path: "/surveys/{surveyId}/responses/{responseId}",
        role: "member/viewer (auth)",
        read: "yes",
        list: "yes",
        create: "no",
        update: "no",
        delete: "no",
        notes: "Read/list permitted if user is in memberIds or has viewer role.",
      },
      {
        path: "/surveys/{surveyId}/responses/{responseId}",
        role: "public respondent",
        read: "no",
        list: "no",
        create: "published surveys only",
        update: "no",
        delete: "no",
      },
    ],
  },
  {
    title: "Network Configs",
    rows: [
      {
        path: "/networkConfigs/{surveyId} and /surveys/{surveyId}/networkConfigs/{configId}",
        role: "owner/editor/admin",
        read: "yes",
        list: "yes",
        create: "yes",
        update: "yes",
        delete: "yes (via write)",
      },
      {
        path: "/networkConfigs/{surveyId} and /surveys/{surveyId}/networkConfigs/{configId}",
        role: "viewer",
        read: "yes",
        list: "yes",
        create: "limited fields",
        update: "limited fields",
        delete: "no",
        notes: "Only source/target/updatedAt fields.",
      },
    ],
  },
  {
    title: "Lists",
    rows: [
      {
        path: "/lists/{listId}",
        role: "authenticated",
        read: "yes",
        list: "yes",
        create: "self owner only",
        update: "owner only",
        delete: "owner only",
      },
      {
        path: "/lists/{listId}/items/{itemId}",
        role: "authenticated",
        read: "yes",
        list: "yes",
        create: "yes",
        update: "list owner only",
        delete: "list owner only",
      },
    ],
  },
  {
    title: "Global Fallback",
    rows: [
      {
        path: "/{document=**}",
        role: "everyone",
        read: "no",
        list: "no",
        create: "no",
        update: "no",
        delete: "no",
        notes: "Default deny for any path not explicitly matched above.",
      },
    ],
  },
];

const FIRESTORE_RULES_SNAPSHOT = `rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {

    match /users/{userId} {
      allow get, list: if request.auth != null;
      allow create, update: if request.auth != null && request.auth.uid == userId;
      allow delete: if false;
    }

    match /surveys/{surveyId} {
      function ownerUid(doc) {
        return doc.data.ownerId != null ? doc.data.ownerId : doc.data.userId;
      }
      function isOwner() {
        return request.auth != null && resource.data.userId == request.auth.uid;
      }
      function isPublished(doc) {
        return (doc.data.status == "published")
            || (doc.data.published == true)
            || (doc.data.isPublished == true);
      }
      function isMember() {
        return request.auth != null
               && (
                    (resource.data.memberIds != null &&
                      resource.data.memberIds.hasAny([request.auth.uid]))
                 || (resource.data.roles[request.auth.uid] in ['viewer','editor'])
                  );
      }
      function isAdmin() {
        return request.auth != null &&
               get(/databases/$(database)/documents/users/$(request.auth.uid))
                 .data.accountRole == "admin";
      }
      function isEditor() {
        return request.auth != null &&
               resource.data.roles[request.auth.uid] == 'editor';
      }

      allow get: if isPublished(resource) || isOwner() || isMember() || isAdmin();
      allow list: if request.auth != null;
      allow create: if request.auth != null
                    && (
                      request.resource.data.userId == request.auth.uid
                      || request.resource.data.ownerId == request.auth.uid
                    );
      allow update: if isAdmin()
                    ||
                    (
                      isOwner()
                      &&
                      ownerUid(request.resource) == ownerUid(resource)
                    )
                    ||
                    (
                      isEditor()
                      &&
                      ownerUid(request.resource) == ownerUid(resource)
                      &&
                      !request.resource.data.diff(resource.data).affectedKeys().hasAny([
                        'userId',
                        'ownerId',
                        'roles',
                        'memberIds'
                      ])
                    );
      allow delete: if isOwner() || isAdmin();

      match /responses/{responseId} {
        allow read, list: if request.auth != null && (
          get(/databases/$(database)/documents/surveys/$(surveyId)).data.userId == request.auth.uid
          || get(/databases/$(database)/documents/surveys/$(surveyId)).data.memberIds.hasAny([request.auth.uid])
          || (get(/databases/$(database)/documents/surveys/$(surveyId)).data.roles[request.auth.uid] in ['viewer','editor'])
          || get(/databases/$(database)/documents/users/$(request.auth.uid)).data.accountRole == "admin"
        );
        allow create: if
          isPublished(get(/databases/$(database)/documents/surveys/$(surveyId)))
          ||
          (
            request.auth != null &&
            (
              get(/databases/$(database)/documents/surveys/$(surveyId)).data.userId == request.auth.uid
              || get(/databases/$(database)/documents/surveys/$(surveyId)).data.roles[request.auth.uid] == 'editor'
              || get(/databases/$(database)/documents/users/$(request.auth.uid)).data.accountRole == "admin"
            )
          );
        allow update: if request.auth != null && (
          get(/databases/$(database)/documents/surveys/$(surveyId)).data.userId == request.auth.uid
          || (get(/databases/$(database)/documents/surveys/$(surveyId)).data.roles[request.auth.uid] == 'editor')
          || get(/databases/$(database)/documents/users/$(request.auth.uid)).data.accountRole == "admin"
        );
        allow delete: if false;
      }

      match /networkConfigs/{configId} {
        allow read: if request.auth != null &&
          (
            get(/databases/$(database)/documents/surveys/$(surveyId)).data.userId == request.auth.uid
            ||
            (get(/databases/$(database)/documents/surveys/$(surveyId)).data.roles[request.auth.uid] in ['viewer','editor'])
            ||
            get(/databases/$(database)/documents/users/$(request.auth.uid)).data.accountRole == "admin"
          );
        allow write: if request.auth != null && (
          (
            get(/databases/$(database)/documents/surveys/$(surveyId)).data.userId == request.auth.uid
            ||
            get(/databases/$(database)/documents/surveys/$(surveyId)).data.roles[request.auth.uid] == 'editor'
            ||
            get(/databases/$(database)/documents/users/$(request.auth.uid)).data.accountRole == "admin"
          )
          ||
          (
            (get(/databases/$(database)/documents/surveys/$(surveyId)).data.roles[request.auth.uid] == 'viewer')
            &&
            request.resource.data.diff(resource.data).affectedKeys().hasOnly(['source','target','updatedAt'])
          )
        );
      }
    }

    match /networkConfigs/{surveyId} {
      allow read: if request.auth != null &&
        (
          get(/databases/$(database)/documents/surveys/$(surveyId)).data.userId == request.auth.uid
          ||
          (get(/databases/$(database)/documents/surveys/$(surveyId)).data.roles[request.auth.uid] in ['viewer','editor'])
          ||
          get(/databases/$(database)/documents/users/$(request.auth.uid)).data.accountRole == "admin"
        );
      allow write: if request.auth != null && (
        (
          get(/databases/$(database)/documents/surveys/$(surveyId)).data.userId == request.auth.uid
          ||
          get(/databases/$(database)/documents/surveys/$(surveyId)).data.roles[request.auth.uid] == 'editor'
          ||
          get(/databases/$(database)/documents/users/$(request.auth.uid)).data.accountRole == "admin"
        )
        ||
        (
          (get(/databases/$(database)/documents/surveys/$(surveyId)).data.roles[request.auth.uid] == 'viewer')
          &&
          request.resource.data.diff(resource.data).affectedKeys().hasOnly(['source','target','updatedAt'])
        )
      );
    }

    match /lists/{listId} {
      allow list, get: if request.auth != null;
      allow create: if request.auth != null && request.resource.data.userId == request.auth.uid;
      allow update, delete: if request.auth != null && resource.data.userId == request.auth.uid;
      match /items/{itemId} {
        allow list, get: if request.auth != null;
        allow create: if request.auth != null;
        allow update, delete: if request.auth != null &&
          get(/databases/$(database)/documents/lists/$(listId)).data.userId == request.auth.uid;
      }
    }

    match /{document=**} {
      allow read, write: if false;
    }
  }
}`;

type AccessLevel = "allowed" | "conditional" | "denied";

function classifyAccess(value: string): AccessLevel {
  const normalized = value.trim().toLowerCase();
  if (normalized === "yes") return "allowed";
  if (normalized === "no") return "denied";
  return "conditional";
}

function accessChip(value: string) {
  const level = classifyAccess(value);
  if (level === "allowed") {
    return <Chip label="Allowed" color="success" size="small" />;
  }
  if (level === "denied") {
    return <Chip label="Denied" color="error" size="small" />;
  }
  return <Chip label="Conditional" color="warning" size="small" />;
}

export default function AdminRules() {
  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="Rules Reference"
        subtitle={`Admin-only reference for Firestore permissions (${firebaseEnv} environment)`}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Matrix is human-friendly guidance. Raw snapshot below is the current rules text from this repo. Enforcement always comes from deployed Firebase rules.
      </Alert>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        <Chip label="Allowed = always permitted for that role" color="success" size="small" />
        <Chip label="Conditional = permitted with constraints" color="warning" size="small" />
        <Chip label="Denied = blocked" color="error" size="small" />
      </Stack>

      <Stack spacing={2}>
        {MATRIX.map((group) => (
          <Paper key={group.title} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>{group.title}</Typography>
            <Stack spacing={2}>
              {Array.from(new Set(group.rows.map((r) => r.path))).map((path) => {
                const rows = group.rows.filter((r) => r.path === path);
                return (
                  <Paper key={`${group.title}-${path}`} variant="outlined" sx={{ overflowX: "auto" }}>
                    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
                      <Typography sx={{ fontFamily: "monospace", fontSize: 13 }}>{path}</Typography>
                    </Box>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Read (get)</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>List (query)</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Create</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Update</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Delete</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row, idx) => (
                          <TableRow key={`${row.role}-${idx}`}>
                            <TableCell>{row.role}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {accessChip(row.read)}
                                {classifyAccess(row.read) === "conditional" && (
                                  <Typography variant="caption" color="text.secondary">{row.read}</Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {accessChip(row.list)}
                                {classifyAccess(row.list) === "conditional" && (
                                  <Typography variant="caption" color="text.secondary">{row.list}</Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {accessChip(row.create)}
                                {classifyAccess(row.create) === "conditional" && (
                                  <Typography variant="caption" color="text.secondary">{row.create}</Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {accessChip(row.update)}
                                {classifyAccess(row.update) === "conditional" && (
                                  <Typography variant="caption" color="text.secondary">{row.update}</Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {accessChip(row.delete)}
                                {classifyAccess(row.delete) === "conditional" && (
                                  <Typography variant="caption" color="text.secondary">{row.delete}</Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {row.notes || "—"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>
        ))}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6">Raw Firestore Rules Snapshot</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Source: repository file <code>firestore.rules</code>.
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "grey.100",
              overflowX: "auto",
              fontSize: 12,
              fontFamily: "monospace",
              whiteSpace: "pre",
            }}
          >
            {FIRESTORE_RULES_SNAPSHOT}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}
